// 세리사무소 포털의 공유 상태.
//
// A단계에서는 홈 하나가 다 들고 있으면 됐다. B단계에서 상단바와 사이드바가 생기며 같은 것을
// 봐야 하는 쪽이 셋으로 늘었다:
//   · 업무 이력 패널의 '오늘' — 방금 승인한 건이 시각과 함께 맨 위에 올라와야 한다(기획서 7.3)
//   · 상단바의 컨텍스트 칩 — 지금 누구의 데이터를 보고 있는지 한순간도 헷갈리면 안 된다(6.1)
//   · 사이드바 — 회사를 고르면 메일·결재·문서가 그 회사 기준으로 재정렬된다(7)
//   · 홈의 검색바 — 여기서 물은 질문이 사이드바의 AI 대화로 들어가야 한다(7.1)
// 넷은 서로 형제라 props 로 이어지지 않는다. 껍데기 상태(열린 패널·고정)까지 여기 둔 이유도
// 같다 — 홈이 "AI 패널을 열어라"라고 말할 수 있어야 한다.
//
// 큐 로직 자체는 Home.zeirishi.work 의 useWorkQueue 에 그대로 있다. 여기서는 그것을
// 한 번만 호출해 트리에 흘려보낸다.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useWorkQueue } from './Home.zeirishi.work';
import type { WorkQueue } from './Home.zeirishi.work';

/** 열려 있는 사이드바 패널 id — null 이면 레일만 보인다 */
export type PanelId = string | null;

/** 첫 화면에 무엇을 먼저 띄울지 — 담당 세무사는 업무, 대표·파트너는 수익을 먼저 본다 */
export type ViewId = 'work' | 'rev';

/** 수익 기회 목록의 갈래 */
export type RevTab = 'all' | '공공' | '금융';

const VIEW_KEY = 'zeirishiView';

/** 저장된 보기 설정을 읽는다 — 모르는 값이거나 접근이 막히면 업무 우선 */
function readView(): ViewId {
  try {
    return localStorage.getItem(VIEW_KEY) === 'rev' ? 'rev' : 'work';
  } catch {
    // 사파리 프라이빗 등에서는 localStorage 접근 자체가 예외를 던진다
    return 'work';
  }
}

export interface ZeirishiContextValue {
  queue: WorkQueue;
  /** 지금 진입해 있는 수임기업 id — null 이면 사무소 전체를 보는 중 */
  currentCo: string | null;
  setCurrentCo: (id: string | null) => void;

  toast: (msg: string) => void;
  /** 현재 떠 있는 토스트 문구 — 실제로 그리는 것은 Layout.zeirishi 다 */
  toastMsg: string | null;

  panel: PanelId;
  /** 같은 id 를 다시 누르면 닫힌다 */
  togglePanel: (id: string) => void;
  openPanel: (id: string) => void;
  closePanel: () => void;
  pinned: boolean;
  togglePin: () => void;

  /** AI 에게 넘길 질문 — seq 가 바뀔 때만 전송된다(같은 문장을 다시 물을 수 있게) */
  aiOutbound: { text: string; seq: number } | undefined;
  /** 질문을 넘기며 AI 패널을 연다. 빈 문자열이면 패널만 연다. */
  askAi: (text: string) => void;

  /** 첫 화면에 먼저 오는 덩어리 — 새로고침해도 유지된다 */
  view: ViewId;
  setView: (v: ViewId) => void;

  /* 수익 기회의 갈래도 여기 둔다 — 보기를 바꾸면 그 섹션이 화면의 다른 자리로 옮겨 가는데,
     컴포넌트 안에 있으면 옮길 때마다 고른 갈래가 풀린다. */
  revTab: RevTab;
  setRevTab: (t: RevTab) => void;
}

const Ctx = createContext<ZeirishiContextValue | null>(null);

export function ZeirishiProvider({ children }: { children: ReactNode }) {
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [currentCo, setCurrentCo] = useState<string | null>(null);
  const [panel, setPanel] = useState<PanelId>(null);
  const [pinned, setPinned] = useState(false);
  const [aiOutbound, setAiOutbound] = useState<{ text: string; seq: number } | undefined>(undefined);
  const askSeq = useRef(0);
  const [view, setViewState] = useState<ViewId>(readView);
  const [revTab, setRevTab] = useState<RevTab>('all');

  /* 토스트 타이머는 하나만 둔다 — 새 메시지가 오면 앞의 타이머를 거둔다 */
  const timerRef = useRef<number | undefined>(undefined);
  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setToastMsg(null), 3400);
  }, []);
  useEffect(() => {
    const timer = timerRef;   // cleanup 에서 ref.current 를 직접 읽지 않는다(린트 규칙)
    return () => window.clearTimeout(timer.current);
  }, []);

  const openPanel = useCallback((id: string) => setPanel(id), []);
  const closePanel = useCallback(() => { setPanel(null); setPinned(false); }, []);

  const togglePanel = useCallback((id: string) => {
    setPanel(prev => {
      if (prev === id) {
        // 패널을 닫으면 고정도 함께 풀린다 — 빈 자리만 남기지 않는다
        setPinned(false);
        return null;
      }
      return id;
    });
  }, []);

  /* 고정은 패널이 떠 있을 때만 의미가 있다 */
  const togglePin = useCallback(() => setPinned(p => !p), []);

  const askAi = useCallback((text: string) => {
    const q = text.trim();
    if (q) {
      askSeq.current += 1;
      setAiOutbound({ text: q, seq: askSeq.current });
    }
    setPanel('ai');
  }, []);

  const setView = useCallback((v: ViewId) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // 저장이 막혀도 이번 세션 동안은 바뀐 순서를 보여준다
    }
  }, []);

  const queue = useWorkQueue(toast);

  const value = useMemo(() => ({
    queue, currentCo, setCurrentCo, toast, toastMsg,
    panel, togglePanel, openPanel, closePanel, pinned, togglePin,
    aiOutbound, askAi, view, setView, revTab, setRevTab,
  }), [queue, currentCo, toast, toastMsg, panel, togglePanel, openPanel, closePanel,
    pinned, togglePin, aiOutbound, askAi, view, setView, revTab]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** 세리사무소 포털 안에서만 쓴다 — 밖에서 부르면 배선이 잘못된 것이라 바로 알려 준다 */
export function useZeirishi(): ZeirishiContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useZeirishi 는 ZeirishiProvider 안에서만 쓸 수 있습니다');
  }
  return v;
}
